import axios from 'axios';
import { API_BASE_URL } from '../api';
import cacheService from '../utils/cacheService';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`
  };
};

// Cache TTL constants (in milliseconds)
const CACHE_TTL = {
  COLLECTIONS_LIST: 5 * 60 * 1000, // 5 minutes
  COLLECTION_DETAIL: 10 * 60 * 1000, // 10 minutes
  PUBLIC_COLLECTIONS: 5 * 60 * 1000, // 5 minutes
  PUBLIC_FABRICS: 10 * 60 * 1000 // 10 minutes
};

export const getFabricCollections = async (includeFabrics = false, search = '', skipCache = false) => {
  const cacheKey = `collections_${includeFabrics}_${search}`;

  if (!skipCache) {
    const cached = cacheService.get(cacheKey);
    if (cached) {
      return cached;
    }
  } else {
    cacheService.delete(cacheKey);
  }
  
  const headers = authHeaders();
  const params = {};
  if (includeFabrics) {
    params.includeFabrics = 'true';
  }
  if (search && search.trim()) {
    params.search = search.trim();
  }
  const res = await axios.get(`${API_BASE_URL}/fabric-collections`, {
    headers,
    params: Object.keys(params).length ? params : undefined,
    timeout: 20000
  });
  
  const data = res.data.data || [];
  
  // Cache the result
  cacheService.set(cacheKey, data, CACHE_TTL.COLLECTIONS_LIST);
  
  return data;
};

export const createFabricCollection = async ({ name, properties = [], fabrics = [], tags = [], imageFile = null, isPublic }) => {
  const headers = {
    ...authHeaders(),
  };

  const formData = new FormData();
  formData.append('name', name);
  formData.append('properties', JSON.stringify(properties));
  if (fabrics && fabrics.length > 0) {
    formData.append('fabrics', JSON.stringify(fabrics));
  }
  if (tags && tags.length > 0) {
    formData.append('tags', JSON.stringify(tags));
  }
  if (typeof isPublic === 'boolean') {
    formData.append('isPublic', isPublic ? 'true' : 'false');
  }
  if (imageFile) {
    formData.append('image', imageFile);
  }

  const res = await axios.post(`${API_BASE_URL}/fabric-collections`, formData, {
    headers,
    timeout: 30000
  });

  // Invalidate collections cache after creating
  cacheService.delete('collections_false_');
  cacheService.delete('collections_true_');

  return res.data.data;
};

export const updateFabricCollection = async (id, { name, properties, fabrics, tags, imageFile, removeImage, isPublic }) => {
  const headers = {
    ...authHeaders(),
  };

  const formData = new FormData();
  if (name !== undefined) {
    formData.append('name', name);
  }
  if (properties !== undefined) {
    formData.append('properties', JSON.stringify(properties));
  }
  if (fabrics !== undefined) {
    formData.append('fabrics', JSON.stringify(fabrics));
  }
  if (tags !== undefined) {
    formData.append('tags', JSON.stringify(tags));
  }
  if (typeof isPublic === 'boolean') {
    formData.append('isPublic', isPublic ? 'true' : 'false');
  }
  if (imageFile) {
    formData.append('image', imageFile);
  }
  if (removeImage) {
    formData.append('removeImage', 'true');
  }

  const res = await axios.put(`${API_BASE_URL}/fabric-collections/${id}`, formData, {
    headers,
    timeout: 30000
  });

  // Invalidate related caches after updating
  cacheService.delete(`collection_${id}_true`);
  cacheService.delete(`collection_${id}_false`);
  cacheService.delete('collections_false_');
  cacheService.delete('collections_true_');

  return res.data.data;
};

export const deleteFabricCollection = async (id) => {
  const headers = authHeaders();
  await axios.delete(`${API_BASE_URL}/fabric-collections/${id}`, {
    headers,
    timeout: 15000
  });
  
  // Invalidate related caches after deleting
  cacheService.delete(`collection_${id}_true`);
  cacheService.delete(`collection_${id}_false`);
  cacheService.delete('collections_false_');
  cacheService.delete('collections_true_');
};

export const getAvailableChildCollections = async (parentId) => {
  const headers = authHeaders();
  const res = await axios.get(`${API_BASE_URL}/fabric-collections/available-children/${parentId}`, {
    headers,
    timeout: 15000
  });
  return res.data.data || [];
};

export const addCollectionAsChild = async (parentId, collectionId) => {
  const headers = {
    ...authHeaders(),
    'Content-Type': 'application/json'
  };
  const res = await axios.put(
    `${API_BASE_URL}/fabric-collections/${parentId}/add-child`,
    { collectionId },
    { headers, timeout: 15000 }
  );
  // Invalidate caches
  cacheService.delete(`collection_${parentId}_true`);
  cacheService.delete(`collection_${parentId}_false`);
  cacheService.delete('collections_false_');
  cacheService.delete('collections_true_');
  return res.data.data;
};

export const removeCollectionFromParent = async (childCollectionId, parentCollectionId = null) => {
  const headers = authHeaders();
  const res = await axios.delete(
    `${API_BASE_URL}/fabric-collections/remove-parent/${childCollectionId}`,
    { headers, timeout: 15000 }
  );
  cacheService.delete('collections_false_');
  cacheService.delete('collections_true_');
  // Invalidate parent's cache so refetch returns updated child list
  if (parentCollectionId) {
    cacheService.delete(`collection_${parentCollectionId}_true`);
    cacheService.delete(`collection_${parentCollectionId}_false`);
  }
  return res.data.data;
};

export const getFabricCollectionById = async (id, includeFabrics = true) => {
  const cacheKey = `collection_${id}_${includeFabrics}`;
  
  // Try to get from cache first
  const cached = cacheService.get(cacheKey);
  if (cached) {
    return cached;
  }
  
  const headers = authHeaders();
  const params = includeFabrics ? { includeFabrics: 'true' } : undefined;
  const res = await axios.get(`${API_BASE_URL}/fabric-collections/${id}`, {
    headers,
    params,
    timeout: 20000
  });
  
  const data = res.data.data;
  
  // Cache the result
  cacheService.set(cacheKey, data, CACHE_TTL.COLLECTION_DETAIL);
  
  return data;
};

// Public route to get fabric collections by phone number (no auth required)
export const getPublicCollectionsByPhone = async (phoneNumber, page = 1, limit = 20, search = '', skipCache = false) => {
  const cacheKey = `public_phone_${phoneNumber}_${page}_${limit}_${search}`;
  
  // Try to get from cache first
  if (skipCache) {
    cacheService.delete(cacheKey);
  } else {
    const cached = cacheService.get(cacheKey);
    if (cached) {
      return cached;
    }
  }
  
  const headers = {
    'Content-Type': 'application/json'
  };
  const params = { page, limit };
  if (search) {
    params.search = search;
  }
  const slug = encodeURIComponent(String(phoneNumber || '').trim().toLowerCase());
  const res = await axios.get(`${API_BASE_URL}/fabric-collections/public/${slug}`, { 
    headers,
    params,
    timeout: 30000 // 30 seconds timeout
  });
  
  const data = res.data.data;
  
  // Cache the result
  cacheService.set(cacheKey, data, CACHE_TTL.PUBLIC_COLLECTIONS);
  
  return data;
};

// Public route to get public fabrics from a collection (no auth required)
export const getPublicFabricsByCollection = async (collectionId, excludeFabricId = null) => {
  const cacheKey = `public_fabrics_${collectionId}_${excludeFabricId || 'none'}`;
  
  // Try to get from cache first
  const cached = cacheService.get(cacheKey);
  if (cached) {
    return cached;
  }
  
  const headers = {
    'Content-Type': 'application/json'
  };
  const params = {};
  if (excludeFabricId) {
    params.excludeFabricId = excludeFabricId;
  }
  const res = await axios.get(`${API_BASE_URL}/fabric-collections/public/collection/${collectionId}/fabrics`, { 
    headers,
    params,
    timeout: 30000
  });
  
  const data = res.data.data;
  
  // Cache the result
  cacheService.set(cacheKey, data, CACHE_TTL.PUBLIC_FABRICS);
  
  return data;
};

// Public route to get a collection and its child collections (no auth required)
export const getPublicCollectionById = async (collectionId) => {
  const headers = { 'Content-Type': 'application/json' };
  const res = await axios.get(`${API_BASE_URL}/fabric-collections/public/collection/${collectionId}`, {
    headers,
    timeout: 30000,
  });
  return res.data?.data || null;
};

// Public route to get collection for a fabric by searching all collections (no auth required)
export const getCollectionByFabric = async (fabricId) => {
  const headers = {
    'Content-Type': 'application/json'
  };
  const res = await axios.get(`${API_BASE_URL}/fabric-collections/public/fabric/${fabricId}/collection`, { 
    headers,
    timeout: 30000
  });
  return res.data.data;
};

// Public route to get all public fabric collections (no auth required)
export const getAllPublicCollections = async (page = 1, limit = 20, search = '') => {
  const cacheKey = `public_collections_${page}_${limit}_${search}`;
  
  // Try to get from cache first
  const cached = cacheService.get(cacheKey);
  if (cached) {
    return cached;
  }
  
  const headers = {
    'Content-Type': 'application/json'
  };
  const params = { page, limit };
  if (search) {
    params.search = search;
  }
  const res = await axios.get(`${API_BASE_URL}/fabric-collections/public/all`, { 
    headers,
    params,
    timeout: 30000
  });
  
  const data = res.data.data;
  
  // Cache the result
  cacheService.set(cacheKey, data, CACHE_TTL.PUBLIC_COLLECTIONS);
  
  return data;
};

// Promoter sets their visibility for a promoted vendor collection (stored in FabricCollection.promoters[])
export const updatePromotedCollectionVisibility = async (collectionId, isPublicStatus) => {
  const headers = {
    ...authHeaders(),
    'Content-Type': 'application/json',
  };
  const res = await axios.patch(
    `${API_BASE_URL}/fabric-collections/${collectionId}/promoter-visibility`,
    { isPublicStatus },
    { headers, timeout: 15000 }
  );
  return res.data?.data;
};

/** Per-fabric visibility for promoter on a supplier collection (FabricCollection.promoters[].fabricPromoterVisibility) */
export const updatePromoterFabricVisibility = async (collectionId, { fabricId, isPublicStatus }) => {
  const headers = {
    ...authHeaders(),
    'Content-Type': 'application/json',
  };
  const res = await axios.patch(
    `${API_BASE_URL}/fabric-collections/${collectionId}/promoter-fabric-visibility`,
    { fabricId, isPublicStatus },
    { headers, timeout: 15000 }
  );
  return res.data?.data;
};

